import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

interface LaTeXRendererProps {
  content: string;
  displayMode?: boolean;
  className?: string;
}

export const LaTeXRenderer: React.FC<LaTeXRendererProps> = ({ 
  content, 
  displayMode = false, 
  className = '' 
}) => {
  // Function to parse content and render LaTeX expressions within HTML
  const renderContent = (text: string) => {
    // First, check if the content contains HTML tags
    const hasHtmlTags = /<[^>]*>/g.test(text);
    
    if (hasHtmlTags) {
      // If content has HTML, we need to parse it differently
      // Split by LaTeX delimiters while preserving HTML structure
      const parts = text.split(/(\$\$.*?\$\$|\$.*?\$)/);
      
      return parts.map((part, index) => {
        // Block math ($$...$$)
        if (part.startsWith('$$') && part.endsWith('$$')) {
          const mathContent = part.slice(2, -2).trim();
          return (
            <BlockMath key={index} math={mathContent} />
          );
        }
        // Inline math ($...$)
        else if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
          const mathContent = part.slice(1, -1).trim();
          return (
            <InlineMath key={index} math={mathContent} />
          );
        }
        // HTML content - render as HTML
        else if (part.trim()) {
          return (
            <span 
              key={index} 
              dangerouslySetInnerHTML={{ __html: part }}
            />
          );
        }
        return null;
      });
    } else {
      // If no HTML tags, use the original logic
      const parts = text.split(/(\$\$.*?\$\$|\$.*?\$)/);
      
      return parts.map((part, index) => {
        // Block math ($$...$$)
        if (part.startsWith('$$') && part.endsWith('$$')) {
          const mathContent = part.slice(2, -2).trim();
          return (
            <BlockMath key={index} math={mathContent} />
          );
        }
        // Inline math ($...$)
        else if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
          const mathContent = part.slice(1, -1).trim();
          return (
            <InlineMath key={index} math={mathContent} />
          );
        }
        // Regular text
        else if (part.trim()) {
          return <span key={index}>{part}</span>;
        }
        return null;
      });
    }
  };

  // If displayMode is true, render the entire content as a single math expression
  if (displayMode) {
    try {
      return (
        <div className={className}>
          <BlockMath math={content} />
        </div>
      );
    } catch (error) {
      console.error('LaTeX rendering error:', error);
      return <div className={className}>{content}</div>;
    }
  }

  // Parse and render mixed content
  try {
    return (
      <div className={className}>
        {renderContent(content)}
      </div>
    );
  } catch (error) {
    console.error('LaTeX rendering error:', error);
    return <div className={className}>{content}</div>;
  }
};

// Hook for easy LaTeX rendering
export const useLaTeX = (content: string, displayMode: boolean = false) => {
  return <LaTeXRenderer content={content} displayMode={displayMode} />;
};
